import React, { useState, useEffect } from 'react';
import api, { BASE_URL, galleryAPI } from '../api';
import { toast } from 'react-toastify';
import './GalleryManager.css'; // External CSS

const GalleryManager = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'classroom',
    order: 0,
    isActive: true
  });

  const categoryOptions = [
    { value: 'classroom', label: 'Classroom', icon: 'fas fa-chalkboard' },
    { value: 'lab', label: 'Lab Session', icon: 'fas fa-flask' },
    { value: 'event', label: 'Event', icon: 'fas fa-calendar-alt' },
    { value: 'workshop', label: 'Workshop', icon: 'fas fa-chalkboard-teacher' },
    { value: 'campus', label: 'Campus', icon: 'fas fa-building' }
  ];

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const response = await galleryAPI.getAllAdmin();
      setGalleryItems(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery items');
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        title: item.title,
        description: item.description || '',
        category: item.category,
        order: item.order || 0,
        isActive: item.isActive
      });
      setImagePreview(item.imageUrl ? `${BASE_URL}${item.imageUrl}` : '');
      setImageFile(null);
    } else {
      setEditingItem(null);
      setFormData({
        title: '',
        description: '',
        category: 'classroom',
        order: 0,
        isActive: true
      });
      setImageFile(null);
      setImagePreview('');
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingItem(null);
    setImageFile(null);
    setImagePreview('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    if (!editingItem && !imageFile) {
      toast.error('Please select an image');
      return;
    }
    
    try {
      if (editingItem) {
        await galleryAPI.update(editingItem._id, formData);
        
        if (imageFile) {
          await galleryAPI.uploadToExisting(editingItem._id, imageFile);
        }
        
        toast.success('Gallery item updated successfully');
      } else {
        await galleryAPI.upload(
          imageFile,
          formData.title,
          formData.description,
          formData.category,
          formData.order,
          formData.isActive
        );
        toast.success('Gallery item added successfully');
      }
      
      handleCloseModal();
      fetchGalleryItems();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this gallery item?')) {
      try {
        await galleryAPI.delete(id);
        toast.success('Gallery item deleted successfully');
        fetchGalleryItems();
      } catch (error) {
        toast.error('Failed to delete gallery item');
        console.error('Delete error:', error);
      }
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      const updatedItem = { ...item, isActive: !item.isActive };
      await galleryAPI.update(item._id, updatedItem);
      toast.success(`Item ${updatedItem.isActive ? 'activated' : 'deactivated'} successfully`);
      fetchGalleryItems();
    } catch (error) {
      toast.error('Failed to update status');
      console.error('Status update error:', error);
    }
  };

  const getCategoryIcon = (category) => {
    const cat = categoryOptions.find(c => c.value === category);
    return cat ? cat.icon : 'fas fa-image';
  };

  const getCategoryLabel = (category) => {
    const cat = categoryOptions.find(c => c.value === category);
    return cat ? cat.label : category;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading gallery...</p>
      </div>
    );
  }

  return (
    <div className="gallery-manager">
      <div className="gallery-header">
        <div className="header-content">
          <div className="header-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="20" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="2.5"/>
              <path d="M21 15L16 10L5 21"/>
            </svg>
          </div>
          <div>
            <h1>Gallery Manager</h1>
            <p>Manage your media library and image collections</p>
          </div>
        </div>
        <button className="btn-add" onClick={() => handleOpenModal()}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add New Image
        </button>
      </div>

      {/* Stats Overview */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="2" y="3" width="20" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="2.5"/>
            </svg>
          </div>
          <div className="stat-info">
            <h3>{galleryItems.length}</h3>
            <p>Total Items</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </div>
          <div className="stat-info">
            <h3>{galleryItems.filter(item => item.isActive).length}</h3>
            <p>Active Items</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
          <div className="stat-info">
            <h3>{new Set(galleryItems.map(item => item.category)).size}</h3>
            <p>Categories</p>
          </div>
        </div>
      </div>

      {/* Gallery Grid */}
      {galleryItems.length > 0 ? (
        <div className="gallery-grid">
          {galleryItems.map((item) => (
            <div key={item._id} className="gallery-card">
              <div className="card-image-wrapper">
                {item.imageUrl ? (
                  <img 
                    src={`${BASE_URL}${item.imageUrl}`} 
                    alt={item.title}
                    className="card-image"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                    }}
                  />
                ) : (
                  <div className="image-placeholder">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="2" y="3" width="20" height="18" rx="2" ry="2"/>
                      <circle cx="8.5" cy="8.5" r="2.5"/>
                    </svg>
                  </div>
                )}
                <div className="card-overlay">
                  <button className="overlay-btn view" onClick={() => window.open(`${BASE_URL}${item.imageUrl}`, '_blank')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                      <circle cx="12" cy="12" r="3"/>
                    </svg>
                  </button>
                  <button className="overlay-btn edit" onClick={() => handleOpenModal(item)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"/>
                      <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"/>
                    </svg>
                  </button>
                  <button className="overlay-btn delete" onClick={() => handleDelete(item._id)}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
                <div className={`status-indicator ${item.isActive ? 'active' : 'inactive'}`}>
                  {item.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="card-content">
                <h3 className="card-title">{item.title}</h3>
                <div className="card-meta">
                  <span className="category">
                    <i className={getCategoryIcon(item.category)}></i>
                    {getCategoryLabel(item.category)}
                  </span>
                  <span className="order">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                    Order: {item.order}
                  </span>
                </div>
                {item.description && (
                  <p className="card-description">{item.description}</p>
                )}
                <div className="card-actions">
                  <button 
                    className={`action-toggle ${item.isActive ? 'deactivate' : 'activate'}`}
                    onClick={() => handleToggleStatus(item)}
                  >
                    {item.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button 
                    className="action-edit"
                    onClick={() => handleOpenModal(item)}
                  >
                    Edit Details
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <rect x="2" y="3" width="20" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="2.5"/>
              <path d="M21 15L16 10L5 21"/>
            </svg>
          </div>
          <h3>No Gallery Items</h3>
          <p>Click "Add New Image" to upload your first gallery image</p>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingItem ? 'Edit Gallery Item' : 'Add New Gallery Item'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input 
                    type="text" 
                    name="title" 
                    value={formData.title} 
                    onChange={handleInputChange} 
                    required 
                    placeholder="Enter image title"
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select name="category" value={formData.category} onChange={handleInputChange} required className="form-select">
                    {categoryOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleInputChange} 
                  rows="3"
                  placeholder="Enter image description (optional)"
                  className="form-textarea"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Order</label>
                  <input 
                    type="number" 
                    name="order" 
                    value={formData.order} 
                    onChange={handleInputChange} 
                    placeholder="Display order"
                    className="form-input"
                  />
                  <small>Lower numbers appear first</small>
                </div>
                <div className="form-group checkbox-group">
                  <label className="checkbox-label">
                    <input 
                      type="checkbox" 
                      name="isActive" 
                      checked={formData.isActive} 
                      onChange={handleInputChange} 
                    />
                    <span>Active (visible on website)</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label>Image {!editingItem && '*'}</label>
                <div className="image-upload-area">
                  {imagePreview ? (
                    <div className="preview-container">
                      <img src={imagePreview} alt="Preview" className="image-preview" />
                      <button 
                        type="button" 
                        className="remove-btn"
                        onClick={() => {
                          setImageFile(null);
                          setImagePreview('');
                        }}
                      >
                        Remove Image
                      </button>
                    </div>
                  ) : (
                    <div className="upload-area">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <p>Click or drag to upload image</p>
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={handleImageChange}
                        required={!editingItem}
                      />
                    </div>
                  )}
                </div>
                <small>Accepted formats: JPG, PNG, GIF, WebP. Max size: 5MB</small>
              </div>

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingItem ? 'Update Image' : 'Upload Image'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;